import React from "react";
import { Link } from "react-router-dom";
import { FaInstagram, FaYoutube, FaEnvelope, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { LOGO_URL, SOCIAL, EMAILS } from "@/lib/brand";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import FooterLogoRail from "@/components/LegalMarks";

export default function Footer() {
    return (
        <footer
            className="border-t border-white/5 bg-[var(--crewz-bg)] mt-32"
            data-testid="site-footer"
        >
            <div className="px-6 md:px-10 lg:px-16 py-20">
                <FooterLogoRail locale="en" />

                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-6">
                        <p className="font-body text-sm text-zinc-400 max-w-md leading-relaxed">
                            An institution rooted in Riyadh — devoted to the culture of exceptional machines and the people who care for them.
                        </p>
                    </div>

                    <div className="md:col-span-3">
                    <p className="overline mb-5 text-zinc-500">Explore</p>
                    <ul className="font-body text-sm space-y-3 text-zinc-300">
                        <li>
                            <Link to="/about" className="hover:text-[var(--crewz-gold)] transition-colors">
                                About
                            </Link>
                        </li>
                        <li>
                            <Link to="/gathering" className="hover:text-[var(--crewz-gold)] transition-colors">
                                <EventName />
                            </Link>
                        </li>
                        <li>
                            <Link to="/membership" className="hover:text-[var(--crewz-gold)] transition-colors">
                                Membership
                            </Link>
                        </li>
                        <li>
                            <Link to="/sponsors" className="hover:text-[var(--crewz-gold)] transition-colors">
                                Sponsors
                            </Link>
                        </li>
                        <li>
                            <Link to="/rules" className="hover:text-[var(--crewz-gold)] transition-colors">
                                Rules &amp; Regulations
                            </Link>
                        </li>
                        <li>
                            <Link to="/privacy" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-privacy">
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link to="/account-deletion" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-account-deletion">
                                Account Deletion
                            </Link>
                        </li>
                        <li>
                            <Link to="/terms" className="hover:text-[var(--crewz-gold)] transition-colors" data-testid="footer-terms">
                                Terms of Service
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <p className="overline mb-5 text-zinc-500">Contact</p>
                    <ul className="font-body text-sm space-y-3 text-zinc-300">
                        <li>
                            <a
                                href={`mailto:${EMAILS.primary}`}
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-email-primary"
                            >
                                <FaEnvelope size={17} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight">{EMAILS.primary}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${EMAILS.admin}`}
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                            >
                                <FaEnvelope size={17} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight">{EMAILS.admin}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href={SOCIAL.registration}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2.5 min-w-0 max-w-full hover:text-[var(--crewz-gold)] transition-colors"
                                data-testid="footer-registration"
                            >
                                <FaArrowUpRightFromSquare size={16} className="shrink-0 text-zinc-400" /> <span className="break-all leading-tight">apply.crewznation.com</span>
                            </a>
                        </li>
                    </ul>

                    <div className="flex flex-col items-start gap-3 mt-6 text-zinc-300">
                        <a
                            href={SOCIAL.instagram}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram — @CrewZNatioN"
                            className="inline-flex items-center gap-2.5 hover:text-[var(--crewz-gold)] transition-colors"
                            data-testid="footer-instagram"
                        >
                            <FaInstagram size={26} className="shrink-0" style={{ color: "#E1306C" }} />
                            <span className="font-body text-sm leading-tight" dir="ltr">@CrewZNatioN</span>
                        </a>
                        <a
                            href={SOCIAL.youtube}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="YouTube — @CrewZNatioN"
                            className="inline-flex items-center gap-2.5 hover:text-[var(--crewz-gold)] transition-colors"
                            data-testid="footer-youtube"
                        >
                            <FaYoutube size={26} className="shrink-0" style={{ color: "#FF0000" }} />
                            <span className="font-body text-sm leading-tight" dir="ltr">@CrewZNatioN</span>
                        </a>
                    </div>
                </div>
                </div>
            </div>

            <div className="border-t border-white/5 px-6 md:px-10 lg:px-16 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <p className="font-body text-xs text-zinc-600">
                    © {new Date().getFullYear()} <Brand />. All rights reserved.
                </p>
                <p
                    className="font-body text-xs text-zinc-500 tracking-[0.18em] uppercase"
                    data-testid="footer-signature-expressions"
                >
                    Smiles Per Liter <span className="text-[var(--crewz-gold)] mx-2">·</span> Eat <span className="mx-1">·</span> Sleep <span className="mx-1">·</span> CrewZ <span className="mx-1">·</span> Repeat
                </p>
                <p className="font-body text-xs text-zinc-600">
                    Riyadh · Saudi Arabia
                </p>
            </div>
        </footer>
    );
}
